import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import css from './StatsSummary.module.css';

import { ReactComponent as DonkeyImg } from './donkey.svg';
import { ReactComponent as UnicornImg } from './unicorn.svg';

import { useSelector } from 'react-redux';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { ArrowStatefulIcon } from '@mm/icons';
import { colors } from '../../../../styles/colors';
import { Button, Spinner } from '@mm/components';
import { StoreState } from '@mm/state/store-interfaces';
import { IAutomationStats } from '@mm/state/model/AutomationModel';

const getTitle = (key: keyof IAutomationStats) => {
  switch (key) {
    case 'messagesSentCount':
      return 'Messages Sent';
    case 'inQueueCount':
      return 'In Queue';
    case 'inAutomationCount':
      return 'In Automation';
    case 'finishedCount':
      return 'Finished';
    case 'engagedCount':
      return 'Engaged';
    case 'unsubscribedCount':
      return 'Unsubscribed';
    case 'contactsCount':
      return 'Contacts';
  }
};

interface Props<T> {
  className?: string;
  campaignId: number;
  onClose?: () => void;
  loading?: boolean;

  contactsCount?: number;
  leadsWithIntent?: number;
  onRefreshClick?: () => void;

  items: T[];
  mapper: {
    getNumber: (v: T) => string;
    getPercent: (v: T) => string;
    getLabel: (v: T) => string;
  };
}

const formatContactsCount = (num: number) => {
  switch (num) {
    default:
      return num;
  }
};

const formatLeadsWithIntent = (num: number) => {
  switch (num) {
    default:
      return num;
  }
};

const StatsSummaryTemplate = <T extends unknown>({
  className,
  onClose,
  items = [],
  mapper,
  contactsCount = 0,
  leadsWithIntent = 0,
  loading = false,
  onRefreshClick,
}: Props<T>) => {
  const contactsCountFormatted = formatContactsCount(contactsCount);
  const leadsWithIntentFormatted = formatLeadsWithIntent(leadsWithIntent);

  const [compact, setCompact] = useState(true);

  return (
    <div
      className={classNames(className, css.container, {
        [css.compact]: true,
      })}
      onClick={() => compact && setCompact(false)}
    >
      {loading && <Spinner className={css.spinner} standalone size={24} />}

      {!loading && (
        <div className={css.content}>
          <div className={css.summary}>
            <div className={css.info}>
              <span className={css.title}>Donkey Land</span>
              <span className={css.count}>
                <span className={css.value}>{contactsCountFormatted}</span>
                <span className={css.placeholder}>Contacts</span>
              </span>
            </div>
          </div>

          <span className={css.arrow} />

          <div className={css.unicorn}>
            <div className={css.info}>
              <span className={css.title}>Unicorn Land</span>
              <span className={css.count}>
                <span className={css.value}>{leadsWithIntentFormatted}</span>
                <span className={css.placeholder}>0% Leads with Intent</span>
              </span>
            </div>
          </div>
          <div className={css.hide} onClick={() => setCompact(true)}>
            <Button
              css={{ container: css.button }}
              color="transparent-no-shadow"
              leftIcon={
                <span className={css.icon}>
                  <ArrowStatefulIcon
                    size={14}
                    direction={compact ? 'down' : 'up'}
                    color={colors.accentBlue}
                  />
                </span>
              }
            >
              {compact ? 'View details' : 'Hide details'}
            </Button>
          </div>
        </div>
      )}

      {!!items.length && mapper && !compact && (
        <div className={css.bottom}>
          <div className={css.stats}>
            {items.map(item => (
              <div className={css.statRow} key={mapper.getLabel(item)}>
                <div className={css.line}>
                  <span className={css.circle} />
                </div>

                <div className={css.figure}>
                  <span className={css.number}>
                    {mapper.getNumber(item)}
                    <span className={css.percent}>
                      {mapper.getPercent(item)}
                    </span>
                  </span>

                  <span className={css.label}>{mapper.getLabel(item)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const StatsSummary = <T extends unknown>(
  props: Pick<
    Props<T>,
    'campaignId' | 'onClose' | 'className' | 'onRefreshClick'
  >,
) => {
  const campaign = useSelector(state =>
    selectCampaignById(state, props.campaignId),
  );

  const automation = useSelector((state: StoreState) => state.automation);

  const stats = campaign && automation.stats[campaign.id];
  const loading = !!(
    campaign && automation.busyStatsCampaignIds.includes(campaign.id)
  );

  const items = stats
    ? [
        'messagesSentCount',
        'inQueueCount',
        'inAutomationCount',
        'finishedCount',
        // 'engagedCount',
        'unsubscribedCount',
      ].map(key => ({
        label: getTitle(key as keyof IAutomationStats),
        number: stats[key] || 0,
      }))
    : [];

  const [stat] = items;

  const mapper = useMemo<Props<typeof stat>['mapper']>(
    () => ({
      getNumber: item => item.number.toString(),
      getLabel: item => item.label.toString(),
      getPercent: () => '',
    }),
    [],
  );

  return (
    <StatsSummaryTemplate
      {...props}
      items={items}
      mapper={mapper}
      loading={loading}
      contactsCount={stats?.contactsCount || 0}
      leadsWithIntent={0}
    />
  );
};
